import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, AlertOctagon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { enumLabel } from '../../../utils/i18n-enums';
import type { SourceAssessmentResult } from '../../../types/analysis-session';
import { HeadlineCard } from './HeadlineCard';
import { renderSourceEditorial } from '../details/source';

const trustAccent = (s: number): string =>
  s >= 70 ? '#22c55e' : s >= 40 ? '#eab308' : s >= 20 ? '#f97316' : '#ef4444';

const trustIcon = (s: number): LucideIcon => {
  if (s >= 70) return ShieldCheck;
  if (s >= 40) return ShieldAlert;
  if (s >= 20) return AlertTriangle;
  return AlertOctagon;
};

interface Props {
  result: SourceAssessmentResult;
}

export const SourceResults: React.FC<Props> = ({ result: r }) => {
  const { t, i18n } = useTranslation();
  const isRo = (i18n.language || '').toLowerCase().startsWith('ro');
  const accent = trustAccent(r.trust_score);
  const Icon = trustIcon(r.trust_score);

  const buildTldr = (): string => {
    const verdictTxt = enumLabel(r.verdict || '').replace(/_/g, ' ').toLowerCase();
    const riskTxt = enumLabel(r.risk_level || '').toLowerCase();
    const verdictForTldr = isRo
      ? verdictTxt
      : verdictTxt.charAt(0).toUpperCase() + verdictTxt.slice(1);
    const riskSuffix = riskTxt ? t('analysis.source.riskSuffix', { risk: riskTxt }) : '';
    return t('analysis.source.tldr', {
      verdictTxt: verdictForTldr,
      score: r.trust_score,
      riskSuffix,
    });
  };

  return (
    <>
      <HeadlineCard
        accent={accent}
        score={r.trust_score}
        scoreLabel={t('analysis.source.trustSuffix')}
        eyebrow={t('analysis.source.assessment')}
        icon={Icon}
        category={enumLabel(r.verdict || '').replace(/_/g, ' ')}
        descriptor={
          <>
            {r.risk_level && (
              <span>{t('analysis.source.riskLabel')} {enumLabel(r.risk_level).toLowerCase()}</span>
            )}
            {r.publication?.name && (
              <>
                <span className="sep">·</span>
                <span>{r.publication.name}</span>
              </>
            )}
          </>
        }
        tldr={buildTldr()}
      />

      {renderSourceEditorial(r, t)}
    </>
  );
};
